import React, { useEffect, useMemo, useState, useRef } from 'react';
import PolymerBarChart from '@/components/charts/PolymerBarChart';
import PolymerLineChart, { LineDataset } from '@/components/charts/PolymerLineChart';
import { getRtiPetchem } from '@/services/peService';
import { useTheme } from '@/contexts/ThemeContext';
import { useRsColors } from '@/hooks/useRsColors';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatMonthKey = (key: string): string => {
  const [y, m] = key.split('-');
  const monthIndex = Number(m) - 1;
  if (!MONTH_NAMES[monthIndex] || !y) return key;
  return `${MONTH_NAMES[monthIndex]} ${y}`;
};

interface RefineryRatesViewProps {
  refineryData?: any[];
}

const RefineryRatesView: React.FC<RefineryRatesViewProps> = ({ refineryData = [] }) => {
  const { isDark } = useTheme();
  const rs = useRsColors();
  const [data, setData] = useState<any[]>(refineryData);
  const hasFetched = useRef(false);
  const [isSmallGrid, setIsSmallGrid] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    setIsSmallGrid(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmallGrid(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchData = async () => {
      try {
        const result = await getRtiPetchem();
        setData(Array.isArray(result) ? result : []);
      } catch (error) {
        console.error('Error fetching refinery data:', error);
        setData([]);
      }
    };
    if (refineryData.length === 0) {
      fetchData();
    } else {
      setData(refineryData);
    }
  }, [refineryData]);

  const sorted = useMemo(
    () =>
      [...data].sort(
        (a, b) =>
          new Date(a.date || a.record_date).getTime() -
          new Date(b.date || b.record_date).getTime(),
      ),
    [data],
  );

  const dailyLabels = useMemo(
    () => sorted.map((d) => String(d.date || d.record_date).slice(0, 10)),
    [sorted],
  );

  const usDataset: LineDataset[] = useMemo(
    () => [
      {
        label: 'US',
        data: sorted.map((d) => (d.us != null ? Number(d.us) : null)),
      },
    ],
    [sorted],
  );

  const padd3Dataset: LineDataset[] = useMemo(
    () => [
      {
        label: 'PADD3',
        data: sorted.map((d) => (d.padd3 != null ? Number(d.padd3) : null)),
      },
    ],
    [sorted],
  );

  const { monthLabels, usMonthly, padd3Monthly } = useMemo(() => {
    const buckets = new Map<string, { usTotal: number; usCount: number; padd3Total: number; padd3Count: number }>();
    data.forEach((d) => {
      const key = String(d.date || d.record_date).slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(key)) return;
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { usTotal: 0, usCount: 0, padd3Total: 0, padd3Count: 0 };
        buckets.set(key, bucket);
      }
      if (d.us != null && d.us !== '') {
        const n = Number(d.us);
        if (Number.isFinite(n)) {
          bucket.usTotal += n;
          bucket.usCount += 1;
        }
      }
      if (d.padd3 != null && d.padd3 !== '') {
        const n = Number(d.padd3);
        if (Number.isFinite(n)) {
          bucket.padd3Total += n;
          bucket.padd3Count += 1;
        }
      }
    });

    const sortedKeys = Array.from(buckets.keys()).sort();
    return {
      monthLabels: sortedKeys.map(formatMonthKey),
      usMonthly: sortedKeys.map((k) => {
        const b = buckets.get(k)!;
        return b.usCount > 0 ? b.usTotal / b.usCount : 0;
      }),
      padd3Monthly: sortedKeys.map((k) => {
        const b = buckets.get(k)!;
        return b.padd3Count > 0 ? b.padd3Total / b.padd3Count : 0;
      }),
    };
  }, [data]);

  return (
    <div className={`grid ${isSmallGrid ? 'grid-cols-1' : 'grid-cols-2'} gap-4 ${isSmallGrid ? 'p-0 -mx-6' : ''}`}>
      <div className={`border border-rs-jet-light rounded-lg ${isSmallGrid ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallGrid ? 'px-4 pt-4 text-center' : ''}`}>
          U.S. Refinery Data
        </h3>
        {sorted.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-rs-off-white/40 text-sm">
            No data
          </div>
        ) : (
          <div className={`h-[350px] ${isSmallGrid ? 'px-4 pb-4' : ''}`}>
            <PolymerLineChart
              datasets={usDataset}
              labels={dailyLabels}
              yLabel="%"
              showRangeSlider
              className="w-full h-full"
            />
          </div>
        )}
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${isSmallGrid ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallGrid ? 'px-4 pt-4 text-center' : ''}`}>
          PADD3 Refinery Data
        </h3>
        {sorted.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-rs-off-white/40 text-sm">
            No data
          </div>
        ) : (
          <div className={`h-[350px] ${isSmallGrid ? 'px-4 pb-4' : ''}`}>
            <PolymerLineChart
              datasets={padd3Dataset}
              labels={dailyLabels}
              yLabel="%"
              showRangeSlider
              className="w-full h-full"
            />
          </div>
        )}
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${isSmallGrid ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallGrid ? 'px-4 pt-4 text-center' : ''}`}>
          U.S. Average per Month
        </h3>
        {monthLabels.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center text-rs-off-white/40 text-sm">
            No data
          </div>
        ) : (
          <div className={`h-[350px] ${isSmallGrid ? 'px-4 pb-4' : ''}`}>
            <PolymerBarChart
              data={usMonthly}
              labels={monthLabels}
              label="U.S. Average"
              yAxisLabel="%"
              xTickStride={6}
              className="w-full h-full"
              color="#80ed40"
            />
          </div>
        )}
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${isSmallGrid ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallGrid ? 'px-4 pt-4 text-center' : ''}`}>
          PADD3 Average per Month
        </h3>
        {monthLabels.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center text-rs-off-white/40 text-sm">
            No data
          </div>
        ) : (
          <div className={`h-[350px] ${isSmallGrid ? 'px-4 pb-4' : ''}`}>
            <PolymerBarChart
              data={padd3Monthly}
              labels={monthLabels}
              label="PADD3 Average"
              yAxisLabel="%"
              xTickStride={6}
              className="w-full h-full"
              color="#80ed40"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RefineryRatesView;